import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/button";
import FitnessToolsResultCard from "../../../components/cards/FitnessToolsResultCard";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import Input from "../../../components/input";
import Loader from "../../../components/loader";
import OtherCalculatorSection from "../../../components/OtherCalculatorSection";
import useChartPeriod from "../../../hooks/useChartPeriod";
import {
  useCreateHRMutation,
  useGetHRQuery,
  useUpdateHRMutation,
} from "../../../redux/Apis/Calculator/HRC";
import { useStaticData } from "../../../utils/data";
import { setMetaTags } from "../../../utils/helper";
import { useTranslate } from "../../../context/TranslationContext";

const HeartRateCalculator = () => {
  const { t } = useTranslate("heartRate");
  const lang = useSelector((state) => state.language.lang);
  const { user } = useSelector((state) => state.authSlice);
  const { CALCULATORS, CALCULATOR_DATA } = useStaticData();
  const location = useLocation();
  const navigate = useNavigate();
  const [age, setAge] = useState("");
  const [restingHR, setRestingHR] = useState("");
  const [activity, setActivity] = useState("");
  const [targetHR, setTargetHR] = useState(null);
  const [mhr, setMHR] = useState(null);
  const [activityZone, setActivityZone] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [createHRC] = useCreateHRMutation();
  const [updateHRC] = useUpdateHRMutation();
  const {
    data: hrcHistoryData,
    refetch: refetchHRC,
    isLoading,
  } = useGetHRQuery({ lang }, { skip: !user });
  const { period, dateRange, handlePeriodChange } = useChartPeriod();
  const { startDate, endDate } = dateRange;

  const {
    data: hrcData,
    refetch: refetchHrcData,
    isLoading: loadingHrc,
  } = useGetHRQuery(
    { startdate: startDate, enddate: endDate, lang },
    { skip: !startDate || !endDate || !user }
  );

  useEffect(() => {
    if (user) {
      refetchHRC();
    }
  }, [user, refetchHRC]);

  useEffect(() => {
    if (startDate && endDate && user) {
      refetchHrcData();
    }
  }, [startDate, endDate, user, refetchHrcData]);

  useEffect(() => {
    setMetaTags(
      "Traineego | Buy or Create Automated Courses & Tracks for Fitness & Wellness",
      "Create, manage, and sell your own courses and tracks, or buy high-quality content. Empower your career through automation and passive income today!"
    );
  }, []);

  useEffect(() => {
    if (hrcHistoryData) {
      setHistoryData(hrcHistoryData.data || []);
    }
  }, [hrcHistoryData]);

  const calculateTargetHR = () => {
    if (!age) {
      toast.error(t("messages.ageError"));
      return;
    }

    const maxHR = 220 - Number(age);
    setMHR(maxHR);

    let zoneResult = null;
    const selected = CALCULATOR_DATA.ACTIVITY_RANGE.find(
      (item) => item.value === activity
    );
    if (selected) {
      const [low, high] = selected.range;
      const min = Math.round(maxHR * low);
      const max = Math.round(maxHR * high);
      zoneResult = `${min} – ${max}`;
      setActivityZone(zoneResult);
    } else {
      setActivityZone(null);
    }

    let target = null;
    if (restingHR) {
      const targetLow = (
        (maxHR - Number(restingHR)) * 0.5 +
        Number(restingHR)
      ).toFixed(0);
      const targetHigh = (
        (maxHR - Number(restingHR)) * 0.7 +
        Number(restingHR)
      ).toFixed(0);
      target = `${targetLow} - ${targetHigh}`;
      setTargetHR(target);
    }

    toast.success(t("messages.saveSuccess"));
    return {
      mhr: maxHR,
      activityZone: zoneResult,
      targetHR: target,
    };
  };

  const handleSaveResult = async (type, result) => {
    if (!result.targetHR) {
      toast.error(t("messages.saveError"));
      return;
    }

    if (!user) {
      toast.error(t("messages.loginFirst"));
      // navigate("/auth/login");
      return;
    }

    try {
      const payload = {
        age: `${age}`,
        rate: `${restingHR}`,
        activity: activity || "",
        result: `${result.targetHR}`,
      };
      if (location?.state?.course) payload.course = location.state.course;
      const response = await createHRC({ payload, params: { lang } }).unwrap();
      toast.success(response.message);
      refetchHRC();
      refetchHrcData();
    } catch (error) {
      console.error("Error saving HRC:", error);
      toast.error(t("messages.saveFailed"));
    }
  };

  const handleDeleteLast = async () => {
    try {
      const latestEntry = [...historyData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )[0];
      if (latestEntry) {
        const payload = {
          id: latestEntry._id,
          isactive: false,
          params: { lang },
        };
        const response = await updateHRC(payload).unwrap();
        toast.success(t("messages.deleteSuccess"));
        refetchHRC();
        refetchHrcData();
      }
    } catch (error) {
      console.error("Error deleting last HRC:", error);
      toast.error(t("messages.deleteError"));
    }
  };

  const toggleHistory = () => setShowHistory((prev) => !prev);

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t("result.notYetCalculated");
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatHistoryEntry = (entry) => {
    const createdAt = new Date(entry.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return (
      <div className="d-flex justify-content-between align-items-center">
        <p className="mb-0">{createdAt}</p>
        <p className="mb-0 fw-bold">{entry.result} bpm</p>
      </div>
    );
  };

  if (isLoading || loadingHrc) {
    <Loader loading={true} />;
  }

  return (
    <>
      <Header />
      <section className="banner" style={{ textAlign: "left" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 d-flex flex-column justify-content-center text-center">
              <h1 className="text-uppercase">{t("title")}</h1>
              <p className="mb-0 p-lg l-grey-text">
                {t("subtitle")}
                <br />
                {t("subtitle2")}
              </p>
            </div>
          </div>
          <div className="row d-flex">
            <div
              className="col-md-12 mt-4 p-5"
              style={{ backgroundColor: "#161617", borderRadius: "2rem" }}
            >
              <div className="row">
                <div className="col-lg-8">
                  <div className="w-100 d-flex text-left">
                    <h4 className="text-uppercase">{t("title")}</h4>
                  </div>
                  <div className="mb-3">
                    <Input
                      label={t("form.ageLabel")}
                      type="number"
                      placeholder={t("form.agePlaceholder")}
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <Input
                      type="select"
                      label={t("form.activityLabel")}
                      value={activity}
                      onChange={(e) => setActivity(e.target.value)}
                      options={CALCULATOR_DATA.ACTIVITY_RANGE}
                    />
                  </div>
                  <div className="mb-3">
                    <Input
                      label={t("form.restingHRLabel")}
                      type="number"
                      placeholder={t("form.restingHRPlaceholder")}
                      value={restingHR}
                      onChange={(e) => setRestingHR(e.target.value)}
                      required
                    />
                  </div>
                  <p className="mb-0 l-grey-text">
                    {t("form.measureInstruction")} <br />
                    {t("form.measureSteps")}
                  </p>
                  <div className="row mt-4">
                    <div
                      className="col-12 bg-white py-2 px-4 d-flex justify-content-between align-items-center"
                      style={{ borderRadius: "0.5rem" }}
                    >
                      <p className="grey-text m-0">
                        {t("form.calculatePrompt")}
                      </p>
                      <Button
                        text={t("form.calculateBtn")}
                        onClick={calculateTargetHR}
                      />
                    </div>
                  </div>
                  {targetHR && location.state?.returnTo && (
                    <div className="text-center mt-3">
                      <Button
                        text={t("cts.continueLearning")}
                        className="site-btn border-btn"
                        style={{ width: "100%", maxWidth: "200px" }}
                        onClick={() =>
                          navigate(location.state.returnTo, {
                            state: {
                              ...location.state,
                              resultKey: `${location.state.sectionIndex}-${location.state.lectureIndex}-HR`,
                              resultData: {
                                type: "HR",
                                result: targetHR,
                                value: `Target HR Zone: ${targetHR}`,
                                timestamp: new Date().toISOString(),
                              },
                            },
                          })
                        }
                      />
                    </div>
                  )}
                </div>
                <div className="col-lg-4 mt-4 mt-lg-0">
                  <div
                    className="h-100 p-3 p-md-4 d-flex flex-column justify-content-start"
                    style={{
                      border: "1px solid white",
                      borderRadius: "1rem",
                      background: "rgba(112,112,112,0.4)",
                    }}
                  >
                    <p className="mb-2" style={{ fontSize: "16px" }}>
                      {mhr
                        ? `${t("result.mhrLabel")} ${mhr} bpm`
                        : t("result.mhrPlaceholder")}
                    </p>
                    {activityZone && (
                      <p className="mb-2" style={{ fontSize: "16px" }}>
                        {t("result.activityZoneLabel")} {activityZone} bpm
                      </p>
                    )}
                    <div
                      className="text-white my-3"
                      style={{
                        backgroundColor: "#161616",
                        borderRadius: "12px",
                        padding: "12px",
                      }}
                    >
                      <h6 className="text-uppercase mb-2 text-center p-md">
                        {t("result.targetZonesTitle")}
                      </h6>
                      <div className="table-responsive">
                        <table
                          className="table text-white text-sm mb-3 text-center p-xs"
                          style={{
                            borderCollapse: "separate",
                            borderSpacing: 0,
                          }}
                        >
                          <thead>
                            <tr className="grey-text">
                              <th
                                style={{
                                  borderTop: "none",
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1.5px solid #bbbbbb",
                                }}
                              >
                                Zone
                              </th>
                              <th
                                style={{
                                  borderTop: "none",
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1.5px solid #bbbbbb",
                                }}
                              >
                                {t("result.percentOfMax")}
                              </th>
                              <th
                                style={{
                                  borderTop: "none",
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1.5px solid #bbbbbb",
                                }}
                              >
                                Notes
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td
                                style={{
                                  color: "#32CD31",
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1px solid #bbbbbb",
                                }}
                              >
                                Zone 1
                              </td>
                              <td
                                style={{
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1px solid #bbbbbb",
                                }}
                              >
                                50-60%
                              </td>
                              <td
                                style={{
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1px solid #bbbbbb",
                                }}
                              >
                                {t("result.zone1Note")}
                              </td>
                            </tr>
                            <tr>
                              <td
                                className="yellow-text"
                                style={{
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1px solid #bbbbbb",
                                }}
                              >
                                Zone 2
                              </td>
                              <td
                                style={{
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1px solid #bbbbbb",
                                }}
                              >
                                60-70%
                              </td>
                              <td
                                style={{
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1px solid #bbbbbb",
                                }}
                              >
                                {t("result.zone2Note")}
                              </td>
                            </tr>
                            <tr>
                              <td
                                className="yellow-text"
                                style={{
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1px solid #bbbbbb",
                                }}
                              >
                                Zone 3
                              </td>
                              <td
                                style={{
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1px solid #bbbbbb",
                                }}
                              >
                                70-80%
                              </td>
                              <td
                                style={{
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "1px solid #bbbbbb",
                                }}
                              >
                                {t("result.zone3Note")}
                              </td>
                            </tr>
                            <tr>
                              <td
                                className="red-text"
                                style={{
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "none",
                                }}
                              >
                                Zone 4
                              </td>
                              <td
                                style={{
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "none",
                                }}
                              >
                                80-90%
                              </td>
                              <td
                                style={{
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderBottom: "none",
                                }}
                              >
                                {t("result.zone4Note")}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      className="bg-white w-100 p-3 text-center mt-auto"
                      style={{
                        border: "0.5px solid gray",
                        height: "auto",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p className="m-0 text-black">{t("cta.title")}</p>
                      <p className="m-0 grey-text p-sm">{t("cta.subtitle")}</p>
                      <Button text={t("cta.button")} to="/classes" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FitnessToolsResultCard
              type="HRC"
              result={
                targetHR
                  ? { targetHR, timestamp: new Date().toISOString() }
                  : null
              }
              historyData={historyData}
              showHistory={showHistory}
              toggleHistory={toggleHistory}
              handleSaveResult={handleSaveResult}
              handleDeleteLast={handleDeleteLast}
              formatHistoryEntry={formatHistoryEntry}
              formatDateTime={formatDateTime}
              chartData={hrcData?.data || []}
              period={period}
              handlePeriodChange={handlePeriodChange}
            />
          </div>
        </div>
      </section>
      <OtherCalculatorSection
        data={CALCULATORS.filter(
          (item) => item.route !== window.location.pathname
        )}
      />
      <Footer />
    </>
  );
};

export default HeartRateCalculator;
